import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, FileText, Brain, Plus, Database } from "lucide-react";

const QuestionGenerationConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  manualQuestionsCount = 0,
  actionType = "generate", // "generate", "addManually", "addFromQuestionBank"
}) => {
  const handleExistingChoice = () => {
    onConfirm("existing");
  };

  const handleNewChoice = () => {
    onConfirm("new");
  };

  // Get action-specific content
  const getActionContent = () => {
    switch (actionType) {
      case "addManually":
        return {
          title: "Manual Questions Detected",
          description: `Questions have been already added. How would you like to proceed with adding new manual questions?`,
          existingText: "Keep existing questions and add new manual questions to them. All questions will be available for the exam.",
          newText: "Replace existing questions with new manual questions. Your current questions will be removed.",
          existingIcon: FileText,
          newIcon: Plus,
        };
      case "addFromQuestionBank":
        return {
          title: "Manual Questions Detected", 
          description: `Questions have been already added. How would you like to proceed with adding questions from question bank?`,
          existingText: "Keep existing questions and add question bank questions to them. All questions will be available for the exam.",
          newText: "Replace existing questions with questions from question bank. Your current questions will be removed.",
          existingIcon: FileText,
          newIcon: Database,
        };
      default: // "generate"
        return {
          title: "Manual Questions Detected",
          description: `Questions have been already added. How would you like to proceed with AI question generation?`,
          existingText: "Keep existing manual questions and add AI-generated questions to them. Both manual and AI questions will be available for the exam.",
          newText: "Replace existing manual questions with new AI questions. Your manual questions will be removed.",
          existingIcon: FileText,
          newIcon: Brain,
        };
    }
  };

  const content = getActionContent();
  const ExistingIcon = content.existingIcon;
  const NewIcon = content.newIcon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <DialogTitle>{content.title}</DialogTitle>
          </div>
          <DialogDescription className="text-left">
            {content.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <ExistingIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Existing</h4>
                <p className="text-sm text-blue-700">
                  {content.existingText}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-start gap-3">
              <NewIcon className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-purple-900 mb-1">New</h4>
                <p className="text-sm text-purple-700">
                  {content.newText}
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleExistingChoice}
            variant="outline"
            className="flex-1 border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            <ExistingIcon className="h-4 w-4 mr-2" />
            Existing
          </Button>
          <Button
            onClick={handleNewChoice}
            className="flex-1 bg-purple-600 hover:bg-purple-700"
          >
            <NewIcon className="h-4 w-4 mr-2" />
            New
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuestionGenerationConfirmDialog;